import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { umStorage } from '@nexelem/services';

type SavedViewData = {
  name: string;
  description?: string;
  data: string;
};

type SavedViewState = {
  defaultViews: Record<string, string>;
  savedViews: Record<string, Record<string, SavedViewData>>;
  setDefaultView: (view: string, state: string) => void;
  saveView: (tableName: string, viewId: string, data: SavedViewData) => void;
  deleteView: (tableName: string, viewId: string) => void;
};

export const useSaveViewStore = create(
  persist(
    immer<SavedViewState>((set, _get) => {
      return {
        defaultViews: {},
        savedViews: {},
        setDefaultView: (tableName: string, viewId: string) => {
          set((state) => {
            state.defaultViews[tableName] = viewId;
          });
        },
        saveView: (tableName: string, viewId: string, viewData: SavedViewData) => {
          set((state) => {
            if (!state.savedViews[tableName]) {
              state.savedViews[tableName] = {};
            }

            state.savedViews[tableName][viewId] = viewData;
          });
        },
        deleteView: (tableName: string, viewId: string) => {
          set((state) => {
            if (state.defaultViews[tableName] === viewId) {
              state.defaultViews[tableName] = '';
            }

            delete state.savedViews[tableName][viewId];
          });
        },
      };
    }),
    {
      name: 'nexelem-tanstack-table-save-view-store',
      storage: createJSONStorage(() => umStorage),
      version: 2,
      // do not hydrate the store when user is not logged in, hydration is handled in Hydration.tsx component
      skipHydration: true,
    },
  ),
);

export type SelectedSavedViewData = SavedViewData & { id: string };

export const selectSavedViews =
  (tableName: string) =>
  (state: SavedViewState): SelectedSavedViewData[] => {
    const savedViews = state.savedViews[tableName];

    if (savedViews) {
      const entries = Object.entries(savedViews);

      return entries.map(([id, viewData]) => {
        return { id, ...viewData };
      });
    }

    return [];
  };

export const selectDefaultView =
  (tableName: string) =>
  (state: SavedViewState): SelectedSavedViewData | undefined => {
    const defaultViewId = state.defaultViews[tableName];

    if (defaultViewId) {
      const defaultView = state.savedViews[tableName][defaultViewId];

      if (defaultView) {
        return { id: defaultViewId, ...defaultView };
      }
    }
  };
